import React, { useEffect, useMemo, useState } from "react";
import Divider from "antd/es/divider";
import Drawer from "antd/es/drawer";
import Layout from "antd/es/layout";
import styled, { useTheme } from "styled-components";
import AppstoreFilled from "@ant-design/icons/AppstoreFilled";
import CaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import { ChevronRight, CircleHelp, Layers, LogOut } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import QuestionCircleOutlined from "@ant-design/icons/QuestionCircleOutlined";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import cornixHorizontalLogo from "images/svg_logo_horizontal.svg";
import cornixWhiteHorizontalLogo from "images/svg_white_logo_horizontal.svg";
import cornixLogoMark from "images/svg_logo_mark.svg";
import cornixWhiteLogoMark from "images/svg_white_logo_mark.svg";
import NewLookMobileMenu from "common/Home/Sider/NewLookMobileMenu.tsx";
import NewLookSiderFooter from "common/Home/Sider/NewLookSiderFooter.tsx";
import {
  COLLAPSED_DESKTOP_MENU,
  HIDE_GROUP_SELECT_PATHS,
  IS_WHOP,
  MOBILE_MENU,
  PATH_TO_FEATURE_FLAG,
} from "common/constants.ts";
import useMedia from "utils/hooks/useMedia.ts";
import { selectIsDarkMode } from "@shared/slices/common.slice.ts";
import SocialMedia from "common/Home/Sider/SocialMedia.tsx";
import Menu from "common/SideMenu/Menu.tsx";
import {
  useBottomNavigator,
  useIsOnboarding,
  useShowWorkspaceSwitch,
} from "utils/hooks/misc.tsx";
import { zIndexes } from "@shared/common/Themes/AppTheme.ts";
import { MenuItemTextStyled } from "common/Home/Menu/MenuItemText.tsx";
import { StylableButton } from "common/Buttons/Button.tsx";
import localStorageService from "utils/localStorageService.ts";
import { hexToRGB, isWhitelabelSubscriptionType } from "@shared/utils/misc.ts";
import WorkspaceSelector from "common/Home/Sider/WorkspaceSelector.tsx";
import { MenuItemConfig, SiderGroup, WorkspaceToPathsMapping } from "types";
import useWorkspace from "utils/hooks/useWorkspace.ts";
import { getWorkspaceToPath } from "common/Home/Sider/utils.ts";
import QrPopover from "common/Home/Sider/QrPopover.tsx";
import {
  BotType,
  Inset,
  LanguageNameSpaces,
  WorkspaceType,
} from "@shared/types";
import { HEDGING_BOTS_FEATURE_FLAG } from "@shared/common/constants.ts";
import Flex from "common/Boxes/Flex.tsx";
import AdminCommunityBanner from "common/Home/Sider/AdminCommunityBanner.tsx";
import UpgradeButton from "common/Home/Header/UpgradeButton.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import { WORKSPACE_TO_DISPLAY_NAME } from "@shared/features/workspaces/constants.ts";
import { HEADER_HEIGHT } from "common/Home/General/constants.tsx";
import WorkspaceSwitch from "common/Home/Header/WorkspaceSwitch.tsx";
import GroupSelect from "features/admin/group/GroupSelect.tsx";
import CurrencyToggle from "common/Home/Sider/CurrencyToggle.tsx";
import UserAvatar from "common/Avatars/UserAvatar.tsx";
import Text from "common/Typography/Text.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import {
  useDisplayTradeDetails,
  useSubUserPermissions,
  useUserInfo,
} from "@shared/utils/hooks/misc.ts";
import useLogout from "utils/hooks/useLogout.ts";
import DarkModeToggle from "common/Home/Header/DarkModeToggle.tsx";
import NewLookToggle from "features/newLook/NewLookToggle.tsx";
import useCanToggleNewLook from "features/newLook/useCanToggleNewLook.ts";
import { isIOS } from "react-device-detect";
import { showIntercomSpace } from "utils/misc.tsx";
import { AppScreen } from "common/Home/General/types.ts";
import { useTelegramSafeAreaInsets } from "utils/hooks/useTelegramWebApp.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import LanguageSelector from "common/Home/Header/LanguageSelector.tsx";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import useRoutesMatch from "utils/hooks/useRoutesMatch.ts";
import useBottomNavigatorMenuItems from "common/Home/MobileHome/useBottomNavigatorMenuItems.tsx";
import {
  ACCOUNTS_MENU_ITEM,
  ADMIN_GROUP_MANAGEMENT_MENU_ITEM,
  ADMIN_SIGNALS_MENU_ITEM,
  ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
  ADMIN_WORKSPACE_MENU_ITEM_KEY,
  AFFILIATION_MENU_ITEM,
  BUSINESS_PROFILE_MENU_ITEM,
  BILLING_MENU_ITEM,
  BOTS_MENU_ITEMS,
  DASHBOARD_MENU_ITEM,
  FINANCE_MENU_ITEM,
  getBacktestingItemKey,
  getBotItemKey,
  getMyBotItemKey,
  getTradingViewToSignalsMenuItem,
  HELP_MENU_ITEM_KEY,
  INTERCOM_MENU_ITEM_KEY,
  INVOICES_MENU_ITEM,
  MARKETPLACE_MENU_ITEM,
  MCP_MENU_ITEM,
  MY_GROUPS_MENU_ITEM,
  PORTFOLIO_MENU_ITEM,
  SETTINGS_MENU_ITEM,
  SIGNALS_MENU_ITEM,
  SIGNALS_TERMINAL_MENU_ITEM,
  SUPPORT_MENU_ITEM_KEY,
  TRADES_MENU_ITEM,
  TRADING_TERMINAL_MENU_ITEM,
  USERS_MANAGEMENT_MENU_ITEM,
  USER_WORKSPACE_MENU_ITEM_KEY,
  WATCHLIST_MENU_ITEM,
  WORKSPACE_MENU_ITEM_KEY,
} from "common/Home/Sider/MenuItems.tsx";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import { SIDER_MENU_ID, USER_LOGOUT_BUTTON_ID } from "testIds";
import { isSuperuser } from "features/finance/utils.ts";

interface SiderProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (isOpen: boolean) => void;
}

const Sider = ({ mobileMenuOpen, setMobileMenuOpen }: SiderProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const appTheme = useTheme();
  const isNewLook = appTheme.isNewLook;
  const isDarkMode = useSelector(selectIsDarkMode);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const realUser = useSelector(
    sharedLib.selectors.authentication.selectRealUser
  );
  const canToggleNewLook = useCanToggleNewLook();
  const location = useLocation();
  const history = useHistory();
  const [isMenuAnimatingClose, setIsMenuAnimatingClose] = useState(true);
  const { workspace, onWorkspaceChange, isWorkspaceValid, getHomePath } =
    useWorkspace();
  const { userInfoItems, uidItem } = useUserInfo();
  const [logout] = useLogout();
  const { ignoreBottomNavigatorKeys } = useBottomNavigatorMenuItems();

  const useDrawerMenu =
    useMedia([`(max-width: ${MOBILE_MENU})`], [true], false) ||
    user?.is_onboarding ||
    user?.isRestrictedWhopUser();
  const showUpgradeButton =
    useDrawerMenu &&
    !user?.is_onboarding &&
    (workspace === WorkspaceType.DEMO || !user?.isFree());
  const { brandingData, isWhitelabel, allowViewOnlyUsersToViewBots } =
    useBranding();

  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const isLargeMobile = useMedia(["(max-width: 750px)"], [true], false);

  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const MOBILE_SIDER_DRAWER_WIDTH = isBottomNavigator ? 300 : 250;
  const { isReadWrite, isReadOnly } = useSubUserPermissions();

  const defaultCollapse = useMedia(
    [`(max-width: ${COLLAPSED_DESKTOP_MENU})`],
    [true],
    false
  );
  const localStorageDefault =
    localStorageService.getParsedValue(COLLAPSED_MENU_KEY);
  const [collapsed, setIsCollapsed] = useState(
    localStorageDefault === null || defaultCollapse
      ? defaultCollapse
      : Boolean(localStorageDefault)
  );

  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location]);

  const handleSetIsCollapsed = (isCollapsed: boolean) => {
    setIsCollapsed(isCollapsed);
    localStorageService.setValue(COLLAPSED_MENU_KEY, isCollapsed);
  };

  useEffect(() => {
    if (defaultCollapse) handleSetIsCollapsed(defaultCollapse);
  }, [defaultCollapse]);

  useEffect(() => {
    if (collapsed) {
      setIsMenuAnimatingClose(false);
      setTimeout(() => setIsMenuAnimatingClose(true), 300);
    }
  }, [collapsed]);

  const displayTradeDetails = useDisplayTradeDetails();
  const isOnboarding = useIsOnboarding();
  const isAdminUser = user?.is_admin || user?.is_whop_creator;
  const showWorkspaceSelector =
    !user?.is_sub_user && isAdminUser && (!collapsed || useDrawerMenu);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const isHideGroupSelectorPath = !!useRoutesMatch(HIDE_GROUP_SELECT_PATHS);
  const showGroupSelect =
    !isHideGroupSelectorPath && isAdminWorkspace && useDrawerMenu;

  const getUserInterfaceClassName = () => {
    if (workspace === WorkspaceType.USER || workspace === WorkspaceType.DEMO)
      return "ant-menu-item-selected";
    if (
      !isWorkspaceValid(WorkspaceType.USER) &&
      !isWorkspaceValid(WorkspaceType.DEMO)
    )
      return "menu-item-disabled";
  };

  const getWorkspaceMenuItem = () => {
    let subMenu: MenuItemConfig[] = [];
    if (isAdminUser) {
      subMenu.push({
        key: ADMIN_WORKSPACE_MENU_ITEM_KEY,
        name: WORKSPACE_TO_DISPLAY_NAME[WorkspaceType.ADMIN] || "",
        onClick: () => onWorkspaceChange(WorkspaceType.ADMIN),
        className:
          workspace === WorkspaceType.ADMIN
            ? "ant-menu-item-selected"
            : undefined,
      });
    }
    subMenu.push({
      key: USER_WORKSPACE_MENU_ITEM_KEY,
      name: WORKSPACE_TO_DISPLAY_NAME[WorkspaceType.USER] || "",
      onClick: () => {
        if (workspace === WorkspaceType.ADMIN)
          onWorkspaceChange(WorkspaceType.USER);
      },
      className: getUserInterfaceClassName(),
    });

    return {
      key: WORKSPACE_MENU_ITEM_KEY,
      icon: (
        <IconWrapper>
          <Flex $flexDirection="column">
            <LookIcon
              classic={<AppstoreFilled />}
              newLook={<Layers size={16} strokeWidth={1.75} />}
            />
            <MenuDivider style={{ transform: "translate(0, -4px)" }} />
          </Flex>
        </IconWrapper>
      ),
      name: "workspace.workspaces",
      subMenu,
    };
  };

  const helpMenuItem = {
    key: HELP_MENU_ITEM_KEY,
    icon: (
      <LookIcon
        classic={<QuestionCircleOutlined />}
        newLook={<CircleHelp size={16} strokeWidth={1.75} />}
      />
    ),
    name: "sider.help",
    subMenu: [
      {
        key: "intercom_links",
        name: "sider.how_to_use",
        onClick: () => showIntercomSpace("help"),
      },
      {
        key: "support",
        name: "sider.support",
        onClick: () => showIntercomSpace("home"),
      },
    ],
    group: SiderGroup.HELP,
  };

  const getMenuItems = () => {
    let menuItems: MenuItemConfig[];
    if (isAdminUser && workspace === WorkspaceType.ADMIN) {
      menuItems = workspaceToMenuItems[WorkspaceType.ADMIN];
    } else {
      menuItems = workspaceToMenuItems[WorkspaceType.USER];
    }
    if (collapsed && !mobileMenuOpen) {
      if (!isWhitelabel) {
        menuItems = [...menuItems, helpMenuItem];
      }
      if (isAdminUser && !useDrawerMenu) {
        menuItems = [getWorkspaceMenuItem(), ...menuItems];
      }
    }
    if (isBottomNavigator) {
      menuItems = menuItems.filter((item) => {
        return !ignoreBottomNavigatorKeys.includes(item.key as any);
      });
    }
    return menuItems;
  };

  const testMissingFeatureFlag = (path?: string) => {
    if (!path) return false;
    const featureFlag = PATH_TO_FEATURE_FLAG[path];
    return featureFlag && (!user || !user?.hasFeatureFlag(featureFlag));
  };

  const getMenuBaseKey = (menuItem: MenuItemConfig) =>
    menuItem.key.replace("-title", "");

  const testReadOnlyLimited = (menuItem: MenuItemConfig) => {
    const menuBaseKey = getMenuBaseKey(menuItem);
    return (
      isReadOnly &&
      !SUB_USER_READ_ONLY_KEYS.has(menuBaseKey) &&
      (!allowViewOnlyUsersToViewBots || !SUB_USER_BOTS_KEYS.has(menuBaseKey))
    );
  };

  const testReadWriteLimited = (menuItem: MenuItemConfig) => {
    const menuBaseKey = getMenuBaseKey(menuItem);
    if (user?.isWhopExperience()) {
      const keys = isAdminWorkspace ? WHOP_ADMIN_KEYS : WHOP_KEYS;
      return !keys.has(menuBaseKey);
    } else if (isReadWrite) {
      return !SUB_USER_READ_WRITE_KEYS.has(menuBaseKey);
    }
  };

  const testSubUserNoClientsLimited = (menuItem: MenuItemConfig) => {
    return (
      user?.is_sub_user &&
      !user?.has_non_deleted_clients_connected &&
      !SUB_USER_NO_CLIENTS_KEYS.has(getMenuBaseKey(menuItem))
    );
  };

  const testNoAccess = (menuItem: MenuItemConfig) => {
    const isReadOnlyLimited = testReadOnlyLimited(menuItem);
    const isReadWriteLimited = testReadWriteLimited(menuItem);
    const isSignalsLimited =
      !displayTradeDetails && menuItem.key === AppScreen.SIGNALS;
    const isHedgingFeatureFlagLimited =
      menuItem.key === getBotItemKey(BotType.HEDGING) &&
      !user?.hasFeatureFlag(HEDGING_BOTS_FEATURE_FLAG);
    const planType =
      user?.is_sub_user && menuItem.key === AppScreen.INVOICES
        ? user.owner_subscription_plan_type
        : user?.subscription_plan_type;
    const isWhitelabelPlansLimited =
      !isWhitelabelSubscriptionType(planType ?? undefined) &&
      !isAdminWorkspace &&
      !(user?.is_sub_user && menuItem.key === AppScreen.INVOICES) &&
      WHITELABEL_PLANS_APP_SCREENS.includes(menuItem.key as any);
    const path = menuItem.routerMatch;
    const isFeatureFlagLimited = Array.isArray(path)
      ? path.some((p) => testMissingFeatureFlag(p))
      : testMissingFeatureFlag(path);
    const isDemoLimited =
      workspace === WorkspaceType.DEMO &&
      [
        AppScreen.INVOICES,
        AppScreen.BUSINESS_PROFILE,
        AppScreen.USERS_MANAGEMENT,
      ].includes(menuItem.key as AppScreen);
    const isSuperuserLimited =
      menuItem.key === AppScreen.FINANCE && !isSuperuser(realUser);

    return (
      isSuperuserLimited ||
      isReadOnlyLimited ||
      isReadWriteLimited ||
      isWhitelabelPlansLimited ||
      isHedgingFeatureFlagLimited ||
      isFeatureFlagLimited ||
      isDemoLimited ||
      testSubUserNoClientsLimited(menuItem) ||
      isSignalsLimited
    );
  };

  const getMenuIcon = (menuItem: MenuItemConfig): React.ReactNode => {
    if (!collapsed || useDrawerMenu || !menuItem.icon) return menuItem.icon;
    return (
      <span
        role="img"
        aria-label={t(
          menuItem.name as TranslationKeys<LanguageNameSpaces.COMMON>
        )}
        style={{ display: "inline-flex" }}
      >
        {menuItem.icon}
      </span>
    );
  };

  const renderMenuItem = (menuItem: MenuItemConfig, isHeader: boolean) => {
    if (testNoAccess(menuItem)) return;
    return {
      key: menuItem.key,
      "aria-label": t(
        menuItem.name as TranslationKeys<LanguageNameSpaces.COMMON>
      ),
      icon: getMenuIcon(menuItem),
      label: ((collapsed && isMenuAnimatingClose) || !collapsed) && (
        <MenuItemTextStyled
          menuHeader={isHeader}
          collapsed={collapsed}
          clickable
          link={menuItem.link}
          name={t(menuItem.name as TranslationKeys<LanguageNameSpaces.COMMON>)}
          subName={menuItem.subName}
          addNewTag={menuItem.isNewFeature}
          featureTag={menuItem.featureTag}
        />
      ),
      onClick: menuItem.onClick,
      className: menuItem.className,
    };
  };

  const renderSubMenu = (
    menuItem: MenuItemConfig,
    filteredSubMenu?: Omit<MenuItemConfig, "subMenu">[]
  ) => {
    let submenuItems: MenuItemConfig[] | undefined = [];
    if (filteredSubMenu && filteredSubMenu.length > 1) {
      submenuItems = [...filteredSubMenu];
    }
    if (collapsed && !useDrawerMenu) {
      submenuItems = [
        {
          key: menuItem.key.concat("-title"),
          name: t(menuItem.name as TranslationKeys<LanguageNameSpaces.COMMON>),
          link: menuItem.link,
        },
        ...submenuItems,
      ];
    }

    return {
      key: menuItem.key,
      icon: getMenuIcon(menuItem),
      label: ((collapsed && isMenuAnimatingClose) || !collapsed) && (
        <MenuItemTextStyled
          menuHeader
          collapsed={collapsed}
          name={t(menuItem.name as TranslationKeys<LanguageNameSpaces.COMMON>)}
          subName={t(
            menuItem.subName as TranslationKeys<LanguageNameSpaces.COMMON>
          )}
          addNewTag={menuItem.isNewFeature}
          featureTag={menuItem.featureTag}
        />
      ),
      onTitleClick: () => {
        if (collapsed && !useDrawerMenu && menuItem.link)
          history.push(menuItem.link);
      },
      children: submenuItems.map((subMenuItem) => {
        return renderMenuItem(subMenuItem, false);
      }),
    };
  };

  const menuItems = getMenuItems();
  const renderMenu = () => {
    const groupNameToChildren: { [key in SiderGroup]?: any[] } = {};
    let processedGroup = false;
    return menuItems.reduce((acc, menuItem) => {
      if (testNoAccess(menuItem)) {
        return acc;
      }
      const filteredSubMenu = menuItem.subMenu?.filter(
        (menuItem) => !testNoAccess(menuItem)
      );
      const menuItemData =
        (filteredSubMenu?.length || 0) > 1 || (collapsed && !useDrawerMenu)
          ? renderSubMenu(menuItem, filteredSubMenu)
          : renderMenuItem(menuItem, true);
      const siderGroup = menuItem.group;
      if (siderGroup && (!isBottomNavigator || processedGroup)) {
        if (siderGroup in groupNameToChildren) {
          groupNameToChildren[siderGroup]?.push(menuItemData);
        } else {
          const children = [menuItemData];
          acc.push({
            key: siderGroup,
            label: collapsed ? <MenuDivider /> : t(siderGroup),
            type: "group",
            children,
          });
          groupNameToChildren[siderGroup] = children;
        }
      } else {
        acc.push(menuItemData);
      }
      if (siderGroup) processedGroup = true;
      return acc;
    }, [] as any);
  };

  const testMenuItem = (menuItem: MenuItemConfig) => {
    const menuItemPaths = Array.isArray(menuItem.routerMatch)
      ? menuItem.routerMatch
      : [menuItem.routerMatch];
    return menuItemPaths.some((path) =>
      matchPath(location.pathname, {
        path: path,
        exact: menuItem.exactMatch ?? false,
        strict: false,
      })
    );
  };

  const getSelectedMenuItemMatchKeyArray = (
    addParentKey?: boolean
  ): string[] => {
    for (let menuItem of menuItems) {
      if (menuItem.subMenu) {
        // A menu with a single visible page renders as the parent item itself
        // (or, collapsed, as a popup holding only its title), so that is the
        // key to select.
        const visibleSubMenu = menuItem.subMenu.filter(
          (item) => !testNoAccess(item)
        );
        const isSinglePage = visibleSubMenu.length <= 1;
        for (let subMenuItem of menuItem.subMenu) {
          if (testMenuItem(subMenuItem)) {
            if (isSinglePage) {
              return collapsed && !useDrawerMenu
                ? [menuItem.key.concat("-title")]
                : [menuItem.key];
            }
            const result = [subMenuItem.key];
            if (addParentKey) {
              result.push(menuItem.key);
            }
            return result;
          }
        }
      }
      if (testMenuItem(menuItem)) {
        return [menuItem.key];
      }
    }

    return [];
  };

  const selectedKeys = getSelectedMenuItemMatchKeyArray();
  const defaultOpenKeys = useMemo(
    () => (collapsed ? undefined : getSelectedMenuItemMatchKeyArray(true)),
    [collapsed]
  );
  const placement = isBottomNavigator || user?.is_onboarding ? "right" : "left";
  const showWorkspaceSwitch = useShowWorkspaceSwitch();

  const getSiderTopSection = () => {
    if (isBottomNavigator) {
      return (
        <MobileTopSectionWrapper>
          {user && (
            <>
              <UserAvatar
                user={user}
                avatarSize={60}
                fallbackInitialsSize={2}
              />
              {user.full_name && (
                <Text
                  $weight={typography.fontWeights.MEDIUM}
                  style={{ marginTop: 12 }}
                >
                  {user.full_name}
                </Text>
              )}
              {user.email && (
                <Text type="secondary" style={{ marginTop: 4 }}>
                  {user.email}
                </Text>
              )}
              {showWorkspaceSwitch && (
                <WorkspaceSwitch style={{ marginTop: 16 }} />
              )}
            </>
          )}
          {showUpgradeButton && (
            <UpgradeButton style={{ alignSelf: "center", marginTop: 16 }} />
          )}
          <WorkspaceWrapper style={{ padding: "16px 0 0 0" }}>
            {showWorkspaceSelector && (
              <WorkspaceSelector workspace={workspace} />
            )}
            {uidItem && (
              <UserInfoItems>
                <Flex key={uidItem.key}>
                  <Text>{uidItem.key}</Text>
                  <Text type={uidItem.type}>{uidItem.value}</Text>
                </Flex>
              </UserInfoItems>
            )}
            {!!userInfoItems.length && (
              <UserInfoItems
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: appTheme.borders.light,
                }}
              >
                {userInfoItems.map((item) => {
                  return (
                    <Flex key={item.key}>
                      <Text>{item.key}</Text>
                      <Text type={item.type}>{item.value}</Text>
                    </Flex>
                  );
                })}
              </UserInfoItems>
            )}
          </WorkspaceWrapper>
          {(!user?.is_onboarding || isAdminWorkspace) && (
            <MenuDivider style={{ marginTop: userInfoItems.length ? 16 : 0 }} />
          )}
          <Flex
            style={{
              flexDirection: "column",
              rowGap: 12,
              width: "100%",
              padding: "12px 0",
            }}
          >
            {!user?.is_onboarding && (
              <>
                <CurrencyToggle style={{ width: "100%" }} />
                {!IS_WHOP && <DarkModeToggle />}
                {canToggleNewLook && <NewLookToggle />}
              </>
            )}
            <LanguageSelector />
          </Flex>
          <MenuDivider />
        </MobileTopSectionWrapper>
      );
    }
    const isHorizontalLogo = isNewLook && !collapsed && !isWhitelabel;
    let logoSrc: string | undefined;
    const isLogoMark = isNewLook && collapsed && !isWhitelabel;
    if (isHorizontalLogo)
      logoSrc = isDarkMode ? cornixWhiteHorizontalLogo : cornixHorizontalLogo;
    else if (isLogoMark)
      logoSrc = isDarkMode ? cornixWhiteLogoMark : cornixLogoMark;
    else logoSrc = isDarkMode ? cornixWhiteLogo : cornixLogo;
    if (isWhitelabel) {
      logoSrc = isDarkMode
        ? brandingData?.dark_mode_display_image
        : brandingData?.light_mode_display_image;
    }
    return (
      <>
        <LogoWrapper
          onClick={() => history.push(getHomePath())}
          style={{ marginBottom: collapsed ? 20 : 0 }}
          $isHorizontal={isHorizontalLogo}
          $isMark={isLogoMark}
        >
          <Logo
            src={logoSrc}
            $isHorizontal={isHorizontalLogo}
            $isMark={isLogoMark}
          />
        </LogoWrapper>
        {showUpgradeButton && (
          <UpgradeButton style={{ alignSelf: "center", marginBottom: 24 }} />
        )}
        {!useDrawerMenu && !defaultCollapse && (
          <CollapseButton
            onClick={() => handleSetIsCollapsed(!collapsed)}
            $collapsed={collapsed}
          >
            <LookIcon
              classic={
                <CaretRightOutlined style={{ marginLeft: 2, fontSize: 14 }} />
              }
              newLook={<ChevronRight size={14} strokeWidth={2} />}
            />
          </CollapseButton>
        )}
        <WorkspaceWrapper style={{ marginTop: collapsed ? 0 : 24 }}>
          {showWorkspaceSelector && <WorkspaceSelector workspace={workspace} />}
          {showGroupSelect && (
            <GroupSelect style={{ marginBottom: 16, width: "100%" }} />
          )}
          {isLargeMobile && showWorkspaceSwitch && (
            <WorkspaceSwitch style={{ marginBottom: 16 }} />
          )}
          {(useDrawerMenu || !collapsed) && !user?.is_onboarding && (
            <MenuDivider />
          )}
        </WorkspaceWrapper>
      </>
    );
  };

  const menu = (
    <StyledMenu
      data-testid={SIDER_MENU_ID}
      defaultSelectedKeys={["/"]}
      selectedKeys={selectedKeys}
      defaultOpenKeys={defaultOpenKeys}
      mode="inline"
      $collapsed={collapsed && !mobileMenuOpen}
      $isBottomNavigator={isBottomNavigator}
      $isNewLook={isNewLook}
      items={renderMenu()}
      style={{
        marginTop: isBottomNavigator || user?.is_onboarding ? 0 : undefined,
      }}
    />
  );

  const adminBanner =
    (!collapsed || useDrawerMenu) && workspace === WorkspaceType.ADMIN ? (
      <AdminCommunityBanner />
    ) : undefined;
  const socialMedia = (!collapsed || useDrawerMenu) &&
    !isWhitelabel &&
    !user?.isWhopExperience() && (
      <SocialMediaWrapper id="app-social-resources">
        <SocialMedia />
      </SocialMediaWrapper>
    );
  const qrPopover = collapsed &&
    !mobileMenuOpen &&
    isMenuAnimatingClose &&
    !isBottomNavigator &&
    !IS_WHOP &&
    !isWhitelabel && <QrPopover isAppStore={isIOS} isCollapsed={true} />;

  const getContent = () => {
    if (
      isBottomNavigator &&
      isNewLook &&
      !isOnboarding &&
      !user?.is_onboarding
    ) {
      return (
        <NewLookMobileMenu
          user={user}
          workspace={workspace}
          menu={menu}
          adminBanner={adminBanner}
          showUpgradeButton={!!showUpgradeButton}
          showWorkspaceSelector={!!showWorkspaceSelector}
          showWorkspaceSwitch={!!showWorkspaceSwitch}
          showGroupSelect={!!showGroupSelect}
          canManagePlan={!testNoAccess(BILLING_MENU_ITEM)}
          showSocial={!!socialMedia}
          isWhitelabel={isWhitelabel}
          onLogout={
            !IS_TELEGRAM_WEB_APP && !IS_WHOP ? () => logout() : undefined
          }
        />
      );
    }
    if (isBottomNavigator || isOnboarding) {
      return (
        <>
          <MobileScrollContent>
            {getSiderTopSection()}
            {menu}
            {!IS_TELEGRAM_WEB_APP && !IS_WHOP && (
              <>
                <MenuDivider style={{ margin: "8px 0" }} />
                <LogoutButton
                  onClick={logout}
                  data-testid={USER_LOGOUT_BUTTON_ID}
                >
                  <LookIcon
                    classic={
                      <LogoutOutlined
                        style={{ marginRight: 26, fontSize: 16 }}
                      />
                    }
                    newLook={
                      <LogOut
                        size={16}
                        strokeWidth={1.75}
                        style={{ marginRight: 26 }}
                      />
                    }
                  />
                  {t("sider.logout")}
                </LogoutButton>
              </>
            )}
            {adminBanner}
          </MobileScrollContent>
          {socialMedia}
          {qrPopover}
        </>
      );
    }
    return (
      <>
        {getSiderTopSection()}
        {menu}
        {adminBanner}
        {isNewLook && user && !user.isWhopExperience() ? (
          <NewLookSiderFooter user={user} collapsed={collapsed} />
        ) : (
          <>
            {socialMedia}
            {qrPopover}
          </>
        )}
      </>
    );
  };

  const content = (
    <InnerWrapper
      $safeAreaInset={safeAreaInset}
      $contentSafeAreaInset={contentSafeAreaInset}
    >
      {getContent()}
    </InnerWrapper>
  );

  const style = isNewLook
    ? { borderRight: "none", background: appTheme.colors.frameBgColor }
    : {
        borderRight: appTheme.borders.lightest,
        background: hexToRGB(appTheme.colors.bgColor, 0.4),
      };

  if (useDrawerMenu) {
    return (
      <StyledDrawer
        push={false}
        placement={placement}
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        styles={{
          wrapper: { width: MOBILE_SIDER_DRAWER_WIDTH },
          body: { padding: 0, background: appTheme.colors.bgColor },
          header: { display: "none" },
        }}
        mask={{ enabled: true, blur: false }}
        style={style}
      >
        {content}
      </StyledDrawer>
    );
  }

  return (
    <StyledMenuWrapper
      collapsible={!useDrawerMenu}
      collapsed={collapsed}
      style={style}
      theme="light"
    >
      {content}
    </StyledMenuWrapper>
  );
};

export default React.memo(Sider);

const COLLAPSED_MENU_KEY = "collapsed_menu";

const StyledDrawer = styled(Drawer)`
  &&& {
    .ant-drawer-body {
      padding-bottom: 0 !important;
    }
  }
`;
const StyledMenuWrapper = styled(Layout.Sider)`
  .ant-layout-sider-children {
    height: 100%;
  }
`;
const InnerWrapper = styled.div<{
  $safeAreaInset?: Inset;
  $contentSafeAreaInset?: Inset;
}>`
  height: 100%;
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${(p) =>
    p.theme.isNewLook ? p.theme.colors.frameBgColor : p.theme.colors.bgColor};
  padding-top: ${(p) =>
    (p.$safeAreaInset?.top ?? 0) + (p.$contentSafeAreaInset?.top ?? 0)}px;
`;
const MobileScrollContent = styled.div`
  overflow-y: auto;
  flex: 1;
`;
const LogoWrapper = styled.div<{ $isHorizontal?: boolean; $isMark?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 16px 0 20px 0;
  flex: none;
  cursor: pointer;
  ${(p) =>
    p.$isHorizontal &&
    `
    justify-content: flex-start;
    align-items: center;
    height: 40px;
    margin: 20px 0 12px;
    padding: 0 22px;
  `}
  ${(p) =>
    p.$isMark &&
    `
    align-items: center;
    height: 40px;
    margin: 20px 0 12px;
    /* Collapsed: the mark sits a little left of centre so the expand
       button on the sider's edge never overlaps it. */
    justify-content: flex-start;
    padding-left: 8px;
  `}
`;
const Logo = styled.img<{ $isHorizontal?: boolean; $isMark?: boolean }>`
  max-width: 85%;
  max-height: ${(p) => (p.$isHorizontal ? 30 : p.$isMark ? 26 : 100)}px;
`;
const SocialMediaWrapper = styled.div`
  width: 100%;
  flex: none;
`;
const MenuDivider = styled(Divider)`
  margin: 0;
  /* New look: the cards' hairline (the classic 12% grey vanished on the
     white light-mode sider; dark mode already read clearly). */
  border-top: ${(p) =>
    p.theme.isNewLook
      ? `1px solid ${p.theme.colors.cardBorderColor}`
      : p.theme.borders.lightest};
`;
const CollapseButton = styled(StylableButton).attrs({
  shape: "circle",
  type: "primary",
  size: "small",
})<{ $collapsed: boolean }>`
  position: absolute;
  left: 100%;
  top: ${HEADER_HEIGHT}px;
  transform: translate(-50%, -50%)
    ${(p) => (!p.$collapsed ? "rotate(180deg)" : "")};
  color: ${(p) => p.theme.colors.basic};
  z-index: ${zIndexes.drawer};
  ${(p) =>
    p.theme.isNewLook &&
    `
    &&& {
      top: 40px;
      color: ${p.theme.colors.secondaryText} !important;
      background: ${p.theme.colors.cardBgColor} !important;
      border: ${p.theme.borders.card} !important;
      box-shadow: ${p.theme.shadows.card};
    }
    &&&:hover {
      color: ${p.theme.colors.primaryBlue} !important;
    }
  `}
`;
const StyledMenu = styled(Menu)<{
  $collapsed: boolean;
  $isBottomNavigator?: boolean;
  $isNewLook?: boolean;
}>`
  ${(p) =>
    !p.$isBottomNavigator &&
    `
    overflow-y: auto;
    overflow-x: hidden;
    flex: auto;
    `};
  margin-top: 24px;
  ${(p) =>
    p.$collapsed &&
    `
    width: 50px;
  `};

  ul::-webkit-scrollbar,
  div::-webkit-scrollbar,
  &::-webkit-scrollbar {
    width: ${(p) => (p.$collapsed ? 6 : 10)}px !important;
  }
  ${(p) =>
    p.$isNewLook &&
    !p.$collapsed &&
    !p.$isBottomNavigator &&
    `
    background: transparent;
    .ant-menu-item,
    .ant-menu-submenu-title {
      margin-inline: 12px;
      width: calc(100% - 24px);
    }
    .ant-menu-item-group-title {
      padding-inline: 24px;
    }
  `}
  ${(p) =>
    p.$isNewLook &&
    p.$collapsed &&
    !p.$isBottomNavigator &&
    `
    /* Scroll shadows: they only appear when the menu is taller than the
       space left for it, so a clipped item never looks like a mistake. */
    background:
      linear-gradient(${
        p.theme.colors.frameBgColor
      } 30%, transparent) center top,
      linear-gradient(transparent, ${
        p.theme.colors.frameBgColor
      } 70%) center bottom,
      radial-gradient(farthest-side at 50% 0, ${hexToRGB(
        p.theme.colors.ink,
        0.12
      )}, transparent) center top,
      radial-gradient(farthest-side at 50% 100%, ${hexToRGB(
        p.theme.colors.ink,
        0.12
      )}, transparent) center bottom !important;
    background-repeat: no-repeat !important;
    background-size: 100% 24px, 100% 24px, 100% 8px, 100% 8px !important;
    background-attachment: local, local, scroll, scroll !important;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    .ant-menu-item-group-title {
      padding: 6px 7px !important;
    }
    .ant-menu-item,
    .ant-menu-submenu-title {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px !important;
      height: 34px !important;
      margin: 1px auto !important;
      padding-inline: 0 !important;
      border: none !important;
      border-radius: ${p.theme.radii.control}px;
      line-height: 34px;
    }
    .ant-menu-item .ant-menu-item-icon,
    .ant-menu-submenu-title .ant-menu-item-icon {
      margin: 0;
    }
    .ant-menu-title-content,
    .ant-menu-submenu-arrow {
      display: none !important;
    }
    .ant-menu-submenu-selected > .ant-menu-submenu-title,
    .ant-menu-item-selected {
      background: ${p.theme.colors.lightBlue};
      color: ${p.theme.colors.primaryBlue};
    }
    /* Selection is the tint alone: no focus ring or border around the
       icon after a click. Keyboard focus shows as a stronger tint. */
    .ant-menu-item,
    .ant-menu-submenu-title {
      outline: none !important;
      box-shadow: none !important;
      border: none !important;
    }
    .ant-menu-item:focus-visible,
    .ant-menu-submenu-title:focus-visible {
      background: ${hexToRGB(p.theme.colors.primaryBlue, 0.16)};
    }
  `}
`;
const IconWrapper = styled.div`
  transform: translate(0, -4px);
`;
const WorkspaceWrapper = styled.div`
  padding: 0 16px;
  width: 100%;
`;
const MobileTopSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 8px 16px;
`;
const UserInfoItems = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;
const LogoutButton = styled.div`
  padding: 0 16px 0 24px;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(p) => p.theme.colors.textColor};
  margin-bottom: 8px;
`;

const SUB_USER_READ_ONLY_KEYS = new Set([
  AppScreen.DASHBOARD,
  AppScreen.INVOICES,
  AppScreen.TRADES,
  AppScreen.PORTFOLIO,
  AppScreen.ACCOUNTS,
  AppScreen.ASSETS,
  AppScreen.POSITIONS,
  AppScreen.SETTINGS,
  AppScreen.ACCOUNT_AND_SECURITY,
  AppScreen.MCP,
  HELP_MENU_ITEM_KEY,
  INTERCOM_MENU_ITEM_KEY,
  SUPPORT_MENU_ITEM_KEY,
]);

const SUB_USER_READ_WRITE_KEYS = new Set([
  ...SUB_USER_READ_ONLY_KEYS,
  getBotItemKey(BotType.SIGNAL),
  getMyBotItemKey(BotType.SIGNAL),
  AppScreen.SIGNALS,
  AppScreen.WATCHLIST,
]);
const SUB_USER_BOTS_KEYS = new Set([
  getBotItemKey(BotType.SIGNAL),
  getBotItemKey(BotType.ASAP),
  getBotItemKey(BotType.GRID),
  getBotItemKey(BotType.TRADING_VIEW),
  getBotItemKey(BotType.HEDGING),
  getMyBotItemKey(BotType.SIGNAL),
  getMyBotItemKey(BotType.ASAP),
  getMyBotItemKey(BotType.GRID),
  getMyBotItemKey(BotType.TRADING_VIEW),
  getMyBotItemKey(BotType.HEDGING),
]);
const WHOP_KEYS = new Set([
  ...[...SUB_USER_READ_WRITE_KEYS].filter(
    (key) =>
      ![
        AppScreen.SETTINGS,
        AppScreen.ACCOUNT_AND_SECURITY,
        AppScreen.INVOICES,
      ].includes(key as AppScreen)
  ),
  getBacktestingItemKey(BotType.SIGNAL),
  AppScreen.WATCHLIST,
  AppScreen.MY_GROUPS,
  WORKSPACE_MENU_ITEM_KEY,
  ADMIN_WORKSPACE_MENU_ITEM_KEY,
  USER_WORKSPACE_MENU_ITEM_KEY,
]);
const WHOP_ADMIN_KEYS = new Set([
  ...WHOP_KEYS,
  AppScreen.TRADINGVIEW_TO_SIGNAL,
  AppScreen.SIGNAL_TERMINAL,
  AppScreen.ADMIN_SIGNALS,
]);

const SUB_USER_NO_CLIENTS_KEYS = new Set([
  AppScreen.DASHBOARD,
  AppScreen.SETTINGS,
  AppScreen.ACCOUNT_AND_SECURITY,
  AppScreen.MCP,
  HELP_MENU_ITEM_KEY,
  INTERCOM_MENU_ITEM_KEY,
  SUPPORT_MENU_ITEM_KEY,
]);

const BASE_USER_WORKSPACE_ITEMS = [
  DASHBOARD_MENU_ITEM,
  TRADES_MENU_ITEM,
  ACCOUNTS_MENU_ITEM,
  PORTFOLIO_MENU_ITEM,
  ...BOTS_MENU_ITEMS,
  TRADING_TERMINAL_MENU_ITEM,
  MCP_MENU_ITEM,
  SIGNALS_MENU_ITEM,
  WATCHLIST_MENU_ITEM,
  INVOICES_MENU_ITEM,
  BUSINESS_PROFILE_MENU_ITEM,
  USERS_MANAGEMENT_MENU_ITEM,
  MY_GROUPS_MENU_ITEM,
  MARKETPLACE_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
  FINANCE_MENU_ITEM,
  BILLING_MENU_ITEM,
  SETTINGS_MENU_ITEM,
];
const ADMIN_WORKSPACE_ITEMS = [
  ADMIN_GROUP_MANAGEMENT_MENU_ITEM,
  ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
  SIGNALS_TERMINAL_MENU_ITEM,
  getTradingViewToSignalsMenuItem(),
  ADMIN_SIGNALS_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
  FINANCE_MENU_ITEM,
  SETTINGS_MENU_ITEM,
];
if (IS_WHOP) {
  ADMIN_WORKSPACE_ITEMS.splice(5, 0, MY_GROUPS_MENU_ITEM);
}
export const workspaceToMenuItems: {
  [workspaceKey in WorkspaceType]: MenuItemConfig[];
} = {
  [WorkspaceType.ADMIN]: ADMIN_WORKSPACE_ITEMS,
  [WorkspaceType.USER]: BASE_USER_WORKSPACE_ITEMS,
  [WorkspaceType.DEMO]: BASE_USER_WORKSPACE_ITEMS,
};

export const workspaceToPaths: WorkspaceToPathsMapping = getWorkspaceToPath();

export const WHITELABEL_PLANS_APP_SCREENS = [
  AppScreen.INVOICES,
  AppScreen.BUSINESS_PROFILE,
  AppScreen.USERS_MANAGEMENT,
  AppScreen.SIGNAL_TERMINAL,
  AppScreen.TRADINGVIEW_TO_SIGNAL,
];
